import { useMemo } from 'react';
import { Listing, ShiurListing, ShiurSession, ShulListing } from '../types';
import { listingToShul } from '../utils/listingConvert';
import { addDaysToDate } from '../utils/israelTime';
import { nextShiur, resolveShiurDays, ShiurContext, ShiurDay, shulMinyanTimes } from '../utils/shiurTimes';
import { useIsraelNow } from './useIsraelNow';
import { useListings } from './useListings';
import { useZmanimGetter } from './useZmanimGetter';

/** Days of zmanim loaded for shiur times: yesterday through two weeks ahead ("next shiur"). */
const ZMANIM_DAYS = 16;

/** The shul a shiur is linked to, among the given listings. */
export function linkedShul(shiur: Listing, listings: Listing[]): ShulListing | undefined {
  if (shiur.category !== 'shiur' || shiur.details?.where !== 'shul' || !shiur.details.shulId) return undefined;
  const found = listings.find((l) => l.id === shiur.details.shulId);
  return found?.category === 'shul' ? found : undefined;
}

/**
 * Lookups for shiurTimes.ts: zmanim (MyZmanim cache, else Hebcal) and, for a
 * shiur in a shul, that shul's minyan times through resolveSchedule().
 */
export function useShiurContext(shul?: ShulListing | null): ShiurContext {
  const now = useIsraelNow();
  const getZmanim = useZmanimGetter(addDaysToDate(now.date, -1), ZMANIM_DAYS);
  return useMemo(() => {
    const asShul = shul ? listingToShul(shul) : undefined;
    return {
      getZmanim,
      ...(asShul ? { getMinyanim: (date: string) => shulMinyanTimes(asShul, date, getZmanim(date)) } : {}),
    };
  }, [getZmanim, shul]);
}

/** A week of a shiur's times from today (shiur page, and the admin's live preview). */
export function useShiurWeek(sessions: ShiurSession[], shul?: ShulListing | null): ShiurDay[] {
  const now = useIsraelNow();
  const ctx = useShiurContext(shul);
  return useMemo(() => resolveShiurDays(sessions, now.date, 7, ctx), [sessions, now.date, ctx]);
}

/** The next time of each shiur (cards and lists), by shiur id. */
export function useNextShiurMap(shiurim: ShiurListing[], listings: Listing[]) {
  const now = useIsraelNow();
  const base = useShiurContext(null);
  return useMemo(() => {
    const out: Record<string, ReturnType<typeof nextShiur>> = {};
    for (const s of shiurim) {
      const shul = linkedShul(s, listings);
      const asShul = shul ? listingToShul(shul) : undefined;
      const ctx: ShiurContext = asShul
        ? { ...base, getMinyanim: (date) => shulMinyanTimes(asShul, date, base.getZmanim(date)) }
        : base;
      out[s.id] = nextShiur(s.details?.sessions ?? [], now, ctx);
    }
    return out;
  }, [shiurim, listings, now, base]);
}

/** The next time of every shiur among `listings` (loads the published shuls they may be linked to). */
export function useShiurNext(listings: Listing[]) {
  const now = useIsraelNow();
  const shiurim = useMemo(() => listings.filter((l): l is ShiurListing => l.category === 'shiur'), [listings]);
  const { listings: shuls } = useListings({ category: 'shul', skip: shiurim.length === 0 });
  const nextById = useNextShiurMap(shiurim, shuls);
  /** A shiur still to come today (the Today filter). */
  const isToday = useMemo(() => (l: Listing) => nextById[l.id]?.date === now.date, [nextById, now.date]);
  return { nextById, isToday, shuls };
}
